'use client';
import { useQuery, useSuspenseQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WISHLIST_QUERIES } from '../../infrastructure/wishlist/utils/queries';
import type { GetWishlistRequest } from '../../infrastructure/wishlist/utils/types';

interface UseGetWishlistOptions {
  enabled?: boolean;
}

/**
 * A suspense read cannot be gated by `enabled`, so the owner is required rather than optional —
 * a keyless read makes the API mint a throwaway guest wishlist. Callers gate on a resolved owner
 * before rendering the component that reads.
 */
type SuspenseWishlistRequest = Required<GetWishlistRequest>;

export const useWishlistService = () => {
  const useGetWishlist = (payload: GetWishlistRequest, { enabled = true }: UseGetWishlistOptions = {}) =>
    useQuery({
      ...WISHLIST_QUERIES.getWishlist(payload),
      // A read must never run without an owner: the API mints a throwaway guest wishlist for keyless reads.
      enabled: enabled && Boolean(payload.customerKey),
    });

  const useSuspenseWishlist = (payload: SuspenseWishlistRequest) =>
    useSuspenseQuery(WISHLIST_QUERIES.getWishlist(payload));

  const useAddProductToWishlist = () => {
    const queryClient = useQueryClient();
    return useMutation(WISHLIST_QUERIES.addProductToWishlist(queryClient));
  };

  const useRemoveProductFromWishlist = () => {
    const queryClient = useQueryClient();
    return useMutation(WISHLIST_QUERIES.removeProductFromWishlist(queryClient));
  };

  const useMigrateWishlist = () => {
    const queryClient = useQueryClient();
    return useMutation(WISHLIST_QUERIES.migrateWishlist(queryClient));
  };

  return {
    useAddProductToWishlist,
    useRemoveProductFromWishlist,
    useMigrateWishlist,
    useGetWishlist,
    useSuspenseWishlist,
  };
};
